import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { ApiClient } from '../../../core/http/api-client';

export interface Tenant {
  id: string;
  name: string;
  displayName: string;
  sortOrder: number;
  isDeactivated: boolean;
  logoImage: string;
  branding: {
    logoImage: string;
    branding: string;
  };
}

/**
 * Service for managing tenant operations.
 * Provides cached access to tenant data to reduce API calls.
 */
@Injectable({
  providedIn: 'root',
})
export class TenantService {
  private api = inject(ApiClient);

  private tenantsCache$: Observable<Tenant[]> | null = null;

  /**
   * Gets all tenants with caching.
   * Uses shareReplay to cache the result and share it among subscribers.
   * Cache is refreshed when the service is re-instantiated (e.g., on app reload).
   *
   * @param forceRefresh - If true, clears cache and fetches fresh data
   * @returns Observable of Tenant array
   */
  getTenants(forceRefresh: boolean = false): Observable<Tenant[]> {
    if (forceRefresh || !this.tenantsCache$) {
      this.tenantsCache$ = this.api.get<Tenant[]>('/Manager/SysAdmin/Tenant').pipe(shareReplay(1));
    }
    return this.tenantsCache$;
  }

  /**
   * Clears the tenants cache, forcing next call to fetch fresh data.
   */
  clearTenantsCache(): void {
    this.tenantsCache$ = null;
  }

  getTenantById(tenantId: string): Observable<Tenant> {
    return this.api.get<Tenant>(`/Manager/SysAdmin/Tenant/${tenantId}`);
  }

  createTenant(tenant: Partial<Tenant>): Observable<Tenant> {
    return this.api.post<Tenant>('/Manager/SysAdmin/Tenant', tenant);
  }

  updateTenant(tenant: Tenant): Observable<Tenant> {
    return this.api.put<Tenant>('/Manager/SysAdmin/Tenant', tenant);
  }

  deleteTenant(id: string): Observable<void> {
    return this.api.delete<void>(`/Manager/SysAdmin/Tenant/${id}`);
  }

  changeTenantStatus(id: string, deactivated: boolean): Observable<void> {
    return this.api.patch<void>(`/Manager/SysAdmin/Tenant/${id}`, {
      deactivate: deactivated,
    });
  }

  getTenantBranding(tenantId: string): Observable<{ logoImage: string; branding: string }> {
    return this.api.get<{ logoImage: string; branding: string }>(
      `/Authenticator/Branding/Tenant/${tenantId}`
    );
  }

  /**
   * Finds a tenant by name from the cached tenants list.
   * Uses the cached tenants to avoid unnecessary API calls.
   *
   * @param name - The name of the tenant to find
   * @param forceRefresh - If true, clears cache and fetches fresh data before searching
   * @returns Observable of Tenant if found, null otherwise
   */
  findTenantByName(name: string, forceRefresh: boolean = false): Observable<Tenant | null> {
    return this.getTenants(forceRefresh).pipe(
      map((tenants) => tenants.find((t) => t.name === name) || null)
    );
  }
}
